import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Slide,
  Button,
  IconButton,
  Typography,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import React from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'

const Transition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Slide direction="down" ref={ref} {...props} />)
Transition.displayName = 'Transition'

type Props = {
  title?: string
  confirmText?: string
  cancelText?: string
  open: boolean
  setOpen: (open: boolean) => void
  onCancel?: () => void
  onClose?: () => void
  onConfirm: () => void
  children: React.ReactNode
}

const ConfirmDialog = (props: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const {
    title = t('general.areYouSure'),
    children,
    confirmText = t('general.yes'),
    onConfirm,
    cancelText = t('general.cancel'),
    onCancel = () => {},
    onClose = () => {},
    open,
    setOpen,
  } = props

  const onConfirmClick = () => {
    setOpen(false)
    onConfirm()
  }

  const onCancelClick = () => {
    setOpen(false)
    onCancel()
  }

  const onCloseInternal = () => {
    setOpen(false)
    onClose()
  }

  return (
    <Dialog
      data-testid="confirm-dialog"
      open={open}
      keepMounted
      onClose={onCloseInternal}
      transitionDuration={300}
      slots={{ transition: Transition }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '100px',
        },
        '& .MuiDialog-paper': {
          borderRadius: '6px',
          padding: 0,
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
          height: 'fit-content',
          marginTop: '100px !important',
          overflow: 'visible',
          maxHeight: 'unset',
          position: 'relative',
        },
      }}
    >
      <DialogContent
        sx={{
          paddingTop: '24px',
          paddingRight: '24px',
          paddingBottom: '16px',
          paddingLeft: '24px',
        }}
      >
        <IconButton
          data-testid="confirm-dialog-close-button"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
        <Typography
          component="h4"
          sx={{
            margin: 0,
            lineHeight: '1.42857143',
            paddingRight: 4,
            fontSize: '1.25rem',
            fontWeight: 'inherit',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 100,
            paddingRight: 4,
            marginTop: 4,
            fontSize: '1.25em',
          }}
        >
          {children}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          padding: 2,
          paddingTop: 0,
          gap: 1,
        }}
      >
        <Button
          data-testid="confirm-dialog-cancel-button"
          onClick={onCancelClick}
          sx={{
            color: 'grey.700',
            fontWeight: 500,
            backgroundColor: 'grey.300',
            '&:hover': {
              backgroundColor: 'grey.400',
            },
            textTransform: 'uppercase',
            minWidth: '80px',
          }}
        >
          {cancelText}
        </Button>
        <Button
          data-testid="confirm-dialog-confirm-button"
          onClick={onConfirmClick}
          color="success"
          variant="contained"
          sx={{
            backgroundColor: '#4caf50',
            '&:hover': {
              backgroundColor: '#43a047',
            },
            fontWeight: 500,
            textTransform: 'uppercase',
            minWidth: '80px',
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
